import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

export type LlmConfigItem = {
  slotKey: string;
  modelName: string;
  provider: string;
  isOverridden: boolean;
  defaultModelName: string;
  defaultProvider: string;
  updatedAt: string | null;
  updatedByUserId: string | null;
};

type LlmConfigResponse = {
  configs: LlmConfigItem[];
};

type LlmConfigAuditItem = {
  slotKey: string;
  previousModelName: string | null;
  previousProvider: string | null;
  newModelName: string;
  newProvider: string;
  changedByUserId: string | null;
  changedAt: string | null;
};

export const useLlmConfig = () => {
  const { data, isLoading, isError, error, refetch } = useQuery<LlmConfigResponse>({
    queryKey: ['admin', 'llm-config'],
  });

  return {
    configs: data?.configs,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useUpdateLlmConfig = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<
    { data: LlmConfigItem },
    Error,
    { slotKey: string; modelName: string; provider: string }
  >({
    mutationFn: ({ slotKey, modelName, provider }) =>
      api.put<LlmConfigItem>(`admin/llm-config/${encodeURIComponent(slotKey)}`, {
        modelName,
        provider,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'llm-config'] });
    },
  });

  const updateConfig = async (slotKey: string, modelName: string, provider: string) => {
    return mutateAsync({ slotKey, modelName, provider });
  };

  return { updateConfig, isLoading };
};

export const useResetLlmConfig = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<unknown, Error, string>({
    mutationFn: (slotKey: string) => api.delete(`admin/llm-config/${encodeURIComponent(slotKey)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'llm-config'] });
    },
  });

  const resetConfig = async (slotKey: string) => {
    return mutateAsync(slotKey);
  };

  return { resetConfig, isLoading };
};

export const useLlmConfigHistory = () => {
  const { data, isLoading, refetch } = useQuery<LlmConfigAuditItem[]>({
    queryKey: ['admin', 'llm-config-history'],
  });

  return {
    history: data,
    isLoading,
    refetch,
  };
};
